import React, { useEffect } from 'react'
import { Button } from '@/components/Buttons/Button'
import { AuthErrorType, LayoutType } from '@/actors/AuthActor/types'
import { TextInput } from '@/components/Inputs'
import { useForm } from 'react-hook-form'
import { useAuth } from '@/hooks/auth/useAuth'
import { useTranslation } from '@/localization/useTranslation'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { AuthSuccessModal } from '@/actors/AuthActor/components/AuthSuccessModal'

import classes from './styles.module.scss'

interface ChangePasswordProps {
  handleBack: () => void
  layoutType: LayoutType | null
  handleLayoutTypeChange: (type: LayoutType | null) => void
  changePasswordHash: string
}

export const ChangePassword: React.FC<ChangePasswordProps> = (props) => {
  const { handleBack, layoutType, handleLayoutTypeChange, changePasswordHash } = props
  const { t } = useTranslation()
  const formTitle = t('Reset password')
  const { changePasswordMutation } = useAuth()

  const { reset, isPending, isSuccess, error } = changePasswordMutation
  const { register, getValues, handleSubmit, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      newPassword: '',
      repeatNewPassword: ''
    }
  })

  useEffect(() => {
    if (layoutType === 'island') return
    handleLayoutTypeChange('single-column')

    return () => {
      handleLayoutTypeChange(null)
    }
  }, [handleLayoutTypeChange, layoutType])

  const handleError = () => {
    reset()
  }

  const handleDismiss = () => {
    handleBack()
  }

  const onSubmit = async (data: { newPassword: string, repeatNewPassword: string }) => {
    try {
      await changePasswordMutation.mutateAsync({ newPassword: data.newPassword, hash: changePasswordHash })
    } catch (err) {
      console.error('Error during change password:', err)
    }
  }

  return (
    <>
      <LoadingOverlay isLoading={isPending} />
      <AuthErrorModal
        error={error as unknown as AuthErrorType}
        handleError={handleError}
      />
      <AuthSuccessModal
        isVisible={isSuccess}
        type="success-change-password"
        onDismiss={handleDismiss}
      />
      <header className={classes.header}>
        <h2 className={classes.title}>{formTitle}</h2>
        <h3 className={classes.subtitle}>{t('Enter your new password')}</h3>
        <h4 className={classes.message}>*{t('Required fields')}</h4>
      </header>
      <form action="#" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <TextInput
            errors={errors}
            label={t('New password')}
            type="password"
            required
            {...register('newPassword', {
              required: {
                value: true,
                message: t('Password is required')
              },
              minLength: {
                value: 6,
                message: t('Password must be at least {minLength} characters long', { minLength: 6 })
              }
            })
            }
          />
          <TextInput
            errors={errors}
            label={t('Repeat new password')}
            type="password"
            required
            {...register('repeatNewPassword', {
              required: {
                value: true,
                message: t('Password is required')
              },
              minLength: {
                value: 6,
                message: t('Password must be at least {minLength} characters long', { minLength: 6 })
              },
              validate: (val: string) => {
                if (getValues('newPassword') !== val) {
                  return t('Passwords do not match')
                }
              }
            })
            }
          />
        </div>
        <Button type="submit" disabled={!isDirty} fullWidth>
          {t('Confirm new password')}
        </Button>
        <Button type="button" onClick={handleBack} variant="secondary" fullWidth>
          {t('Back')}
        </Button>
      </form>
    </>

  )
}
